import { useEffect, useRef, useState } from "react";
import { Check, ChevronDown } from "lucide-react";

const slug = (s) => String(s).toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/(^-|-$)/g, "");

/**
 * On-theme dropdown (navy/orange) matching the date picker's look.
 * Props: value, onChange(value), options ([{value,label}] or strings), testid, placeholder
 */
export const SelectField = ({ value, onChange, options, testid, placeholder = "Select…" }) => {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const opts = options.map((o) => (typeof o === "string" ? { value: o, label: o } : o));
  const current = opts.find((o) => o.value === value);

  useEffect(() => {
    if (!open) return;
    const onDoc = (e) => {
      if (ref.current && !ref.current.contains(e.target)) setOpen(false);
    };
    const onKey = (e) => {
      if (e.key === "Escape") setOpen(false);
    };
    document.addEventListener("mousedown", onDoc);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDoc);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  return (
    <div className="relative" ref={ref}>
      <button
        type="button"
        data-testid={testid}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className="field-input flex items-center justify-between gap-2 text-left w-full"
      >
        <span className={current ? "text-cream truncate" : "text-faded truncate"}>
          {current ? current.label : placeholder}
        </span>
        <ChevronDown size={15} className={`text-gold shrink-0 transition-transform duration-200 ${open ? "rotate-180" : ""}`} />
      </button>
      {open && (
        <div
          role="listbox"
          data-testid={testid ? `${testid}-menu` : undefined}
          className="absolute z-[90] mt-2 w-full max-h-64 overflow-y-auto rounded-2xl border border-gold/25 bg-card shadow-2xl shadow-black/50 py-1.5"
        >
          {opts.map((o) => (
            <button
              key={o.value}
              type="button"
              role="option"
              aria-selected={o.value === value}
              data-testid={testid ? `${testid}-option-${slug(o.value)}` : undefined}
              onClick={() => {
                onChange(o.value);
                setOpen(false);
              }}
              className={`w-full flex items-center justify-between gap-2 px-4 py-2.5 text-sm text-left transition-colors ${
                o.value === value ? "bg-gold/15 text-gold" : "text-mist hover:bg-gold/10 hover:text-cream"
              }`}
            >
              <span className="truncate">{o.label}</span>
              {o.value === value && <Check size={14} className="shrink-0" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
};
